import React from "react";
import { Star, Check } from "lucide-react";

// 7-day micro-strip for habit rows. Renders the last 7 days (oldest → today)
// as tiny dots so the user can feel their consistency at a glance, without
// the existing flat streak counter being the only signal.
//
//   sage filled circle + ✓  → normal completion
//   mustard filled circle + ⭐ → Mochi covered this day (cover pass)
//   soft empty ring         → no completion (never framed as "missed")
//   today gets a subtle ring outline so the strip orients in time
//
// Shame-safe: there is intentionally NO red, NO "missed" tooltip, and the
// covered days are rendered with the SAME prominence as normal completions
// so the cover pass lives on visually as a celebratory event.
//
//   <StreakStrip habit={h} todayKey="2026-05-28" />
export default function StreakStrip({ habit, todayKey }) {
    if (!habit || !todayKey) return null;
    const completed = new Set(habit.completed_dates || []);
    const covered = new Set(habit.covered_dates || []);

    // Build the trailing 7-day window ending on today.
    const today = new Date(todayKey + "T12:00:00");  // noon avoids DST jumps
    const days = [];
    for (let i = 6; i >= 0; i--) {
        const d = new Date(today);
        d.setDate(today.getDate() - i);
        const iso = d.toISOString().slice(0, 10);  // YYYY-MM-DD UTC slice, fine for our chronological labels
        // Use the ymd of `d` directly to avoid any tz drift.
        const localIso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
        days.push({
            iso: localIso,
            isToday: i === 0,
            done: completed.has(localIso),
            covered: covered.has(localIso),
            weekdayShort: d.toLocaleDateString(undefined, { weekday: "short" }).slice(0, 1),
        });
        // ensure no lint about unused `iso`
        void iso;
    }

    return (
        <div
            className="inline-flex items-center gap-1 mt-1.5"
            data-testid={`habit-strip-${habit.id}`}
            aria-label="Last 7 days"
        >
            {days.map((d) => {
                const base = "w-4 h-4 rounded-full flex items-center justify-center transition-colors";
                let cls, content;
                if (d.covered) {
                    cls = "bg-mustard-soft text-mustard";
                    content = <Star size={9} fill="currentColor" strokeWidth={0} />;
                } else if (d.done) {
                    cls = "bg-sage text-cream";
                    content = <Check size={9} strokeWidth={3} />;
                } else {
                    cls = "border border-warm-300/50";
                    content = null;
                }
                const todayRing = d.isToday ? "ring-1 ring-warm-500/50 ring-offset-1 ring-offset-cream" : "";
                const title = d.covered
                    ? "mochi covered this one 🍡"
                    : d.done
                    ? "done"
                    : d.isToday ? "today" : ""; // intentionally blank for past empty days — no shame copy
                return (
                    <span
                        key={d.iso}
                        className={`${base} ${cls} ${todayRing}`}
                        title={title}
                        data-testid={`habit-strip-${habit.id}-${d.iso}`}
                        data-state={d.covered ? "covered" : d.done ? "done" : "empty"}
                    >
                        {content}
                    </span>
                );
            })}
        </div>
    );
}
